// src/components/Effects.jsx
//
// Interaction pieces shared across the page.
//
// Performance rules these all follow: only transform and opacity are
// animated, so everything stays on the compositor and never triggers layout.
// Pointer values are written to motion values or CSS custom properties rather
// than React state, so a mouse move does not re-render the tree. All of it is
// switched off on touch devices, where there is no pointer to track, and
// under prefers-reduced-motion.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";

// ---------------------------------------------------------------------------
function useFinePointer() {
  const [fine, setFine] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = () => setFine(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return fine;
}

// ---------------------------------------------------------------------------
// Tilts a block toward the pointer. Springs are soft and heavily damped so it
// reads as weight rather than a wobble.
// ---------------------------------------------------------------------------
export function Tilt({ children, max = 7, className = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const fine = useFinePointer();
  const active = fine && !reduce;

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 70, damping: 22 });
  const sy = useSpring(my, { stiffness: 70, damping: 22 });
  const rotateY = useTransform(sx, [-1, 1], [-max, max]);
  const rotateX = useTransform(sy, [-1, 1], [max, -max]);

  const onMove = useCallback(
    (e) => {
      if (!active || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
      my.set(((e.clientY - r.top) / r.height) * 2 - 1);
    },
    [active, mx, my]
  );

  const onLeave = useCallback(() => {
    mx.set(0);
    my.set(0);
  }, [mx, my]);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={className}
      style={{ perspective: 1400 }}
    >
      <motion.div
        style={active ? { rotateX, rotateY, transformStyle: "preserve-3d" } : undefined}
      >
        {children}
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// A card with a cold highlight that follows the pointer. The position is
// written straight to CSS custom properties on the node, so React never
// re-renders while the mouse moves.
// ---------------------------------------------------------------------------
export function GlowCard({ children, className = "" }) {
  const ref = useRef(null);
  const frame = useRef(0);
  const reduce = useReducedMotion();
  const fine = useFinePointer();

  const onMove = useCallback(
    (e) => {
      if (!fine || reduce || !ref.current) return;
      // One write per frame at most, whatever the pointer sample rate is.
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty("--gx", `${e.clientX - r.left}px`);
        el.style.setProperty("--gy", `${e.clientY - r.top}px`);
      });
    },
    [fine, reduce]
  );

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      className={`group/glow relative overflow-hidden ${className}`}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-300 group-hover/glow:opacity-100"
        style={{
          background:
            "radial-gradient(220px circle at var(--gx,50%) var(--gy,50%), rgb(var(--bk-led) / 0.16), transparent 70%)",
        }}
      />
      <div className="relative">{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pulls slightly toward the pointer while hovered. Range is deliberately
// small - enough to feel alive, not enough to make the target hard to hit.
// ---------------------------------------------------------------------------
export function Magnetic({ children, strength = 0.28, className = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const fine = useFinePointer();
  const active = fine && !reduce;

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 20, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 260, damping: 20, mass: 0.4 });

  const onMove = useCallback(
    (e) => {
      if (!active || !ref.current) return;
      const r = ref.current.getBoundingClientRect();
      x.set((e.clientX - (r.left + r.width / 2)) * strength);
      y.set((e.clientY - (r.top + r.height / 2)) * strength);
    },
    [active, strength, x, y]
  );

  const onLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={active ? { x: sx, y: sy } : undefined}
      className={`inline-block ${className}`}
    >
      {children}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Cold chain rail.
//
// A fixed hairline down the left edge that runs the page from ambient to
// holding temperature as you scroll - the pulldown a new room goes through on
// commissioning. Scroll progress drives one transform and one integer, so the
// whole thing costs almost nothing.
// ---------------------------------------------------------------------------
const AMBIENT = 22;
const HOLDING = -18;

export function ColdChain() {
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const [temp, setTemp] = useState(AMBIENT);

  const smooth = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 30,
    restDelta: 0.001,
  });

  const markerY = useTransform(smooth, [0, 1], ["0%", "100%"]);
  const fill = useTransform(smooth, [0, 1], ["0%", "100%"]);

  // Rounded, so state changes about forty times over the whole page rather
  // than on every scroll event.
  useMotionValueEvent(smooth, "change", (v) => {
    setTemp(Math.round(AMBIENT + (HOLDING - AMBIENT) * v));
  });

  if (reduce) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed left-6 top-1/2 z-30 hidden -translate-y-1/2 xl:block"
    >
      <div className="flex flex-col items-center gap-4">
        <span className="font-mono text-[8.5px] uppercase tracking-[0.2em] text-subtle">
          Ambient
        </span>

        <div className="relative h-56 w-px bg-line">
          <motion.div
            className="absolute inset-x-0 top-0 bg-gradient-to-b from-brooke-red to-led"
            style={{ height: fill }}
          />
          <motion.div
            className="absolute -left-[3px] h-[7px] w-[7px] rounded-full bg-led shadow-[0_0_10px_rgb(var(--bk-led))]"
            style={{ top: markerY }}
          />
        </div>

        <span className="font-mono text-[8.5px] uppercase tracking-[0.2em] text-subtle">
          Holding
        </span>

        <span className="font-mono text-[13px] font-bold tabular-nums text-led">
          {temp > 0 ? "+" : temp < 0 ? "\u2212" : ""}
          {Math.abs(temp)}
          <span className="text-[0.7em] opacity-70">&deg;C</span>
        </span>
      </div>
    </div>
  );
}